"use client";
import type { SurveyQuestion } from "@/types/survey";
import { useEffect, useState } from "react";

interface TextQuestionProps {
  question: SurveyQuestion;
  brandColor?: string;
  isSelected?: (
    question: SurveyQuestion,
    candidate: string | number,
  ) => boolean;
  onAnswerChange?: (question: SurveyQuestion, value: string) => void;
  isPreview?: boolean;
  selectedValue?: string | number;
  onPreviewAnswerChange?: (questionId: string, value: string | number) => void;
  answer?: string; // For backward compatibility with landing page
  uiStrings?: {
    typeHere: string;
  };
}

export default function TextQuestion({
  question,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  brandColor = "var(--color-alias-info)",
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isSelected,
  onAnswerChange,
  isPreview = false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  selectedValue,
  onPreviewAnswerChange,
  answer,
  uiStrings,
}: TextQuestionProps) {
  const [localAnswer, setLocalAnswer] = useState(answer || "");
  // Keep local state in sync if parent answer prop changes (e.g. prefill/resume)
  useEffect(() => {
    setLocalAnswer(answer || "");
  }, [answer]);
  const handleAnswerChange = (value: string) => {
    if (isPreview && onPreviewAnswerChange) {
      onPreviewAnswerChange(question.id, value);
    } else if (onAnswerChange) {
      onAnswerChange(question, value);
      setLocalAnswer(value);
    }
  };

  const isTextarea = question.questionType === "textarea";

  if (isPreview) {
    // Preview mode - read-only display
    return (
      <div className="mb-4 w-full">
        <label className="block text-sm font-medium mb-2 text-left">
          {question.text}
        </label>
        <textarea
          className="w-full p-3 border rounded-md"
          rows={isTextarea ? 3 : 1}
          placeholder={uiStrings?.typeHere ?? "Type here..."}
          readOnly
          style={{
            borderColor: "var(--color-alias-neutral-light)",
            backgroundColor: "var(--color-alias-white)",
            color: "var(--color-alias-neutral-darkest)",
          }}
        />
      </div>
    );
  }

  // Interactive mode
  return (
    <div className="mb-4 w-full">
      <label className="block text-sm font-medium mb-2 text-left">
        {question.text}
      </label>
      {isTextarea ? (
        <textarea
          className="w-full p-3 border rounded-md"
          rows={3}
          style={{
            borderColor: "var(--color-alias-neutral-light)",
            backgroundColor: "var(--color-alias-white)",
            color: "var(--color-alias-neutral-darkest)",
          }}
          placeholder={uiStrings?.typeHere ?? "Type here..."}
          value={localAnswer}
          onChange={(e) => setLocalAnswer(e.target.value)}
          onBlur={(e) => handleAnswerChange(e.target.value)}
        />
      ) : (
        <input
          type="text"
          className="w-full p-3 border rounded-md"
          placeholder={uiStrings?.typeHere ?? "Type here..."}
          style={{
            borderColor: "var(--color-alias-neutral-light)",
            backgroundColor: "var(--color-alias-white)",
            color: "var(--color-alias-neutral-darkest)",
          }}
          value={localAnswer}
          onChange={(e) => setLocalAnswer(e.target.value)}
          onBlur={(e) => handleAnswerChange(e.target.value)}
        />
      )}
    </div>
  );
}
